import BciLogo from '../assets/images/bci.png';
import BukLogo from '../assets/images/buk.png';
import InternacionalLogo from '../assets/images/internacional.png';
import BerriosLogo from '../assets/images/logos/dealers/berrios.png';
import MiguelJacobHeloLogo from '../assets/images/logos/dealers/migueljacobhelo.png';

export interface CompanyInfo {
  companyName: string;
  logoUrl: string;
  companyType: 'car_dealership' | 'financial_institution' | 'insurance_broker';
}

export interface SubdomainConfig {
  [subdomain: string]: CompanyInfo;
}

/**
 * Subdomain branding configuration.
 *
 * To onboard a new dealer/company at <subdomain>.relif.com:
 *
 * Required (the subdomain won't work without these):
 * 1. Add the DNS record for <subdomain>.relif.com (Cloudflare).
 * 2. Federated sign-in is per-origin (each tenant logs in on its own subdomain), and neither
 *    provider allows wildcards, so register the new origin with BOTH — or SSO breaks there:
 *    - Microsoft (Entra app registration → Authentication → Single-page application):
 *      add redirect URI  https://<subdomain>.relif.com/sign-in  (WITH the /sign-in path).
 *      Missing it → AADSTS50011 on login.
 *    - Google (OAuth 2.0 client → Authorized JavaScript origins, NOT redirect URIs):
 *      add origin  https://<subdomain>.relif.com  (origin only, no path, no trailing slash).
 *
 * Optional (custom logo on the sign-in card; falls back to the default Relif branding otherwise):
 * 3. Add the logo PNG to src/assets/images/logos/dealers/<subdomain>.png
 * 4. Import the logo in this file
 * 5. Add the entry to the subdomainConfig object with companyName, logoUrl and companyType
 */
export const subdomainConfig: SubdomainConfig = {
  // Financial institutions
  bancointernacional: {
    companyName: 'Banco Internacional',
    logoUrl: InternacionalLogo,
    companyType: 'financial_institution',
  },
  bci: {
    companyName: 'BCI',
    logoUrl: BciLogo,
    companyType: 'financial_institution',
  },
  buk: {
    companyName: 'BUK',
    logoUrl: BukLogo,
    companyType: 'financial_institution',
  },
  // Car dealerships
  migueljacobhelo: {
    companyName: 'Miguel Jacob Helo',
    logoUrl: MiguelJacobHeloLogo,
    companyType: 'car_dealership',
  },
  berrios: {
    companyName: 'Berrios',
    logoUrl: BerriosLogo,
    companyType: 'car_dealership',
  },
  // Default
  default: {
    companyName: 'Relif',
    logoUrl: '/logos/zeller-default.png',
    companyType: 'insurance_broker',
  },
};

// Helper function to get company info with fallback
export const getCompanyInfo = (subdomain: string | null): CompanyInfo => {
  if (!subdomain) {
    return subdomainConfig.default;
  }

  return subdomainConfig[subdomain] || subdomainConfig.default;
};

// Helper function to validate if subdomain exists
export const isValidSubdomain = (subdomain: string): boolean => {
  return subdomain in subdomainConfig && subdomain !== 'default';
};
